import React, { ChangeEvent, ReactElement, useMemo, useRef, useState } from 'react';
import { Params, useParams } from 'react-router-dom';

import { Member } from '../../../../../types/entity.types';
import { TableFunctions, TableRowData } from '../../../../../types/common.types';

import { ARCHIVED } from '../../../../../utils/constants';
import { formatUsername } from '../../../../../utils/util';

import Table from '../../../../common/table/Table';
import FilePicker from '../../../../common/input/file-picker/FilePicker';
import Button from '../../../../common/button/Button';
import MemberModal from '../member-modal/MemberModal';

import { ReactComponent as TeamIcon } from '../../../../../resources/icons/leaderboard-team.svg';

import './member-table.scss';

type ColumnFunctions = {
  paid: (selectedMember: Member) => void;
  staff: (selectedMember: Member) => void;
};

type DataTemplate = {
  emailAddress: string;
  phoneNumber: string;
  name: string;
  paid: string;
  team: string;
  username: string;
};

type MappedMember = {
  externalReference: string;
  firstName: string | undefined;
  lastName: string | undefined;
  emailAddress: string;
  phoneNumber: string;
  name: string;
  paid: boolean;
  staff: boolean;
  team: string | undefined;
  username: undefined | string;
};

const DATA_TEMPLATE: DataTemplate = {
  name: '',
  emailAddress: '',
  username: '',
  phoneNumber: '',
  team: '',
  paid: ''
};

const HIDDEN_COLUMNS: string[] = ['externalReference', 'firstName', 'lastName'];

type Props = {
  members: Member[];
  createMember: (memberToSave: Member, onSuccess: () => void) => void;
  updateMember: (memberToSave: Member, close?: () => void) => void;
  deleteMember: (externalReference: string, onSuccess: () => void) => void;
  uploadMemberExcel: (memberExcel: File) => void;
  downloadMemberExcel: (event: React.MouseEvent<HTMLButtonElement>) => void;
  sendMemberActivationCommunications: (externalReference: string, sendSMS: boolean, sendEmail: boolean) => void;
};

const MemberTable = ({
  members,
  createMember,
  updateMember,
  deleteMember,
  uploadMemberExcel,
  downloadMemberExcel,
  sendMemberActivationCommunications
}: Props): ReactElement => {
  const [member, setMember] = useState<Member | undefined>(undefined);

  const fileRef: React.MutableRefObject<HTMLInputElement | null> = useRef<HTMLInputElement | null>(null);

  const { status }: Readonly<Params> = useParams();

  const columnFunctions: TableFunctions = useMemo(
    (): ColumnFunctions => ({
      staff: (selectedMember: Member): void => {
        if (status === ARCHIVED) return;

        const staff: boolean = selectedMember.staff ?? false;

        delete selectedMember.name;
        delete selectedMember.staff;

        const member: Member = {
          ...(selectedMember as Member),
          isStaff: !staff
        };

        updateMember(member);
      },
      paid: (selectedMember: Member): void => {
        if (status === ARCHIVED) return;

        const paid: boolean = selectedMember.paid ?? false;
        const staff: boolean = selectedMember.staff ?? false;

        delete selectedMember.name;
        delete selectedMember.staff;

        const member: Member = {
          ...(selectedMember as Member),
          isStaff: staff ?? false,
          paid: !paid
        };

        updateMember(member);
      }
    }),
    [updateMember, status]
  ) as unknown as TableFunctions;

  const mappedMembers: MappedMember[] = useMemo((): MappedMember[] => {
    const mappedMembers: MappedMember[] = members.map(
      (member: Member): MappedMember => ({
        name: `${member.firstName} ${member.lastName}`,
        emailAddress: member.emailAddress,
        username: formatUsername(member.username),
        phoneNumber: member.phoneNumber,
        team: member.team,
        staff: !!member.isStaff,
        paid: !!member.paid,
        externalReference: member.externalReference ?? '',
        firstName: member.firstName,
        lastName: member.lastName
      })
    );

    return mappedMembers.sort((memberOne: MappedMember, memberTwo: MappedMember): number => {
      if (memberOne.name > memberTwo.name) return 1;
      if (memberOne.name < memberTwo.name) return -1;
      return 0;
    });
  }, [members]);

  const handleMemberClick = (member: TableRowData): void => {
    if (status === ARCHIVED) return;

    setMember(member as Member);
  };

  const renderBottomActions = (): ReactElement => {
    return (
      <div className="member-table__bottom-actions">
        {status !== ARCHIVED && (
          <>
            <Button text="Add Member" onClick={(): void => setMember({} as Member)} />
            <FilePicker
              triggerElement={<Button text="Upload .xlsx" onClick={(): void => fileRef.current?.click()} />}
              fileRef={fileRef}
              onChange={({ target }: ChangeEvent<HTMLInputElement>): void =>
                target.files?.[0] && uploadMemberExcel(target.files[0])
              }
            />
          </>
        )}
        <Button text="Download .xlsx" onClick={downloadMemberExcel} />
      </div>
    );
  };

  const renderTeamIcon = (member: TableRowData): ReactElement => {
    if (!member.team) return <td className="member-table__team" />;

    return (
      <td className="member-table__team">
        <TeamIcon className="member-table__team-icon" />
        <span className="member-table__team-label">{member.team}</span>
      </td>
    );
  };

  return (
    <>
      <section className="member-table" data-testid="member-table">
        <Table
          data={mappedMembers}
          dataTemplate={DATA_TEMPLATE}
          hiddenColumns={HIDDEN_COLUMNS}
          rowsPerPage={10}
          handleRowClick={handleMemberClick}
          columnFunctions={columnFunctions}
          bottomActions={renderBottomActions()}
          columnRenderFunctions={{
            team: renderTeamIcon
          }}
          filter
          onDeleteRowClick={undefined}
        />
      </section>

      <MemberModal
        member={member}
        close={(): void => setMember(undefined)}
        createMember={createMember}
        updateMember={updateMember}
        deleteMember={deleteMember}
        sendMemberActivationCommunications={sendMemberActivationCommunications}
      />
    </>
  );
};

export default MemberTable;
